import {
	restRepository,
	restCmsRepository,
	UpsertReskillRequestDTO,
	CreateLayoffDiscountRequestDto,
	ReskillOrderItemStatus,
	CareerType,
} from "@onair/repositories";
import { unstable_noStore } from "next/cache";

export interface GetReskillsParams {
	offset?: number;
	limit?: number;
	type?: CareerType;
}

export interface GetMyReskillsParams {
	search?: string;
	withExpired?: boolean;
	type?: CareerType;
}

export interface GetReskillOrderItemsParams {
	offset?: number;
	limit?: number;
	search?: string;
	status?: ReskillOrderItemStatus;
	reskillPriceId?: string;
	startDate?: string;
	endDate?: string;
	userId?: string;
}

export const createReskill = async (data: UpsertReskillRequestDTO) => {
	const response = await restRepository.api.createReskill(data);
	return response.data;
};

export const updateReskill = async (
	id: string,
	data: UpsertReskillRequestDTO,
) => {
	const response = await restRepository.api.updateReskill(id, data);
	return response.data;
};

export const deleteReskill = async (id: string) => {
	const response = await restRepository.api.deleteReskill(id);
	return response.data;
};

export const getReskills = async (params: GetReskillsParams) => {
	const response = await restRepository.api.getReskills(params);
	return response.data;
};

export const getReskillById = async (id: string) => {
	const response = await restRepository.api.getReskill(id);
	return response.data;
};

export const getReskillByPriceId = async (priceId: string) => {
	const response = await restRepository.api.getReskillByPriceId(priceId);
	return response.data;
};

export const getMyReskills = async (params?: GetMyReskillsParams) => {
	const response = await restRepository.api.getMyReskills(params);
	return response.data;
};

export const getMyReskill = async (
	id: string,
	params?: GetMyReskillsParams,
) => {
	const response = await restRepository.api.getMyReskill(id, params);
	return response.data;
};

export const getReskillProducts = async (id: string) => {
	const response = await restRepository.api.getReskillProducts(id);
	return response.data;
};

export const getReskillPriceProducts = async (priceId: string) => {
	const response = await restRepository.api.getReskillPriceProducts(priceId);
	return response.data;
};

export const getMyReskillPriceProducts = async (priceId: string) => {
	const response = await restRepository.api.getMyReskillPriceProducts(priceId);
	return response.data;
};

export const getReskillPricesByReskillId = async (id: string) => {
	const response = await restRepository.api.getReskillPrices(id);
	return response.data;
};

export const getReskillOrderItems = async (
	reskillId: string,
	params: GetReskillOrderItemsParams,
) => {
	const response = await restRepository.api.getReskillOrderItems(reskillId, {
		offset: params.offset || 1,
		limit: params.limit || 10,
		search: params.search || undefined,
		status: params.status || undefined,
		reskillPriceId: params.reskillPriceId || undefined,
		startDate: params.startDate || undefined,
		endDate: params.endDate || undefined,
		userId: params.userId || undefined,
	});
	return response.data;
};

export type BaseQueryFilter = {
	sort?: string;
	page?: number;
	pageSize?: number;
	withCount?: boolean;
	start?: number;
	limit?: number;
	fields?: string;
	populate?: string;
	filters?: Record<string, any>;
	locale?: string;
};

export const getReskillPage = async () => {
	unstable_noStore();
	const response = await restCmsRepository.reskill.getReskill({
		// populate: "*",
		"populate[0]": "topHeroContentSection",
		"populate[1]": "topHeroContentSection.bgImage",
		"populate[2]": "boxContentSection",
		"populate[3]": "boxContentSection.items",
		"populate[4]": "boxContentSection.items.thumbnail",
		"populate[5]": "mediaContentSection",
		"populate[6]": "mediaContentSection.items",
		"populate[7]": "mediaContentSection.thumbnail",
		"populate[8]": "heroContentSecondSection",
		"populate[9]": "heroContentSecondSection.bgImage",
		"populate[10]": "contentBoxCards",
		"populate[11]": "contentBoxCards.items",
		"populate[12]": "contentBoxCards.items.thumbnail",
		"populate[13]": "reskillPlansSection",
		"populate[14]": "reskillPlansSection.benefitsPlans.plans.icon",
		// "populate[15]": "reskillPlansSection.benefitsPlans.plans",
		"populate[16]": "reskillPlansSection.pricingPlans",
		"populate[17]": "workableSection",
		"populate[18]": "workableSection.items",
		"populate[19]": "workableSection.items.icon",
		"populate[20]": "workableSection.thumbnail",
		"populate[21]": "journeySection",
		"populate[22]": "journeySection.groupItems",
		"populate[23]": "journeySection.groupItems.bgImage",
		"populate[24]": "journeySection.groupItems.items",
		"populate[25]": "journeySection.groupItems.items.thumbnail",
		"populate[26]": "favoriteCoursesSection",
		"populate[27]": "reskillCardInfo.items",
		"populate[28]": "journeyWaitingSection.bgImage",
		"populate[29]": "careerPathSection.items.thumbnail",
		"populate[30]": "noteSection.thumbnail",
		"populate[31]": "metaSeo.metaThumbnail",
		"populate[32]": "reskillPlansSection.benefitsPlans.plans",
		"populate[33]": "journeySectionV2.bgImage",
		"populate[34]": "reskillPlansSection.thumbnail",
	} as Record<string, string>);
	return response.data;
};
export const getMainReskill = async (params?: { type?: CareerType }) => {
	const response = await restRepository.api.getMainReskill(params);
	return response.data;
};

export const getLayoffDiscountRequest = async () => {
	const response = await restRepository.api.userGetLayoffDiscountRequest();
	return response.data;
};

export const createLayoffDiscountRequest = async (
	payload: CreateLayoffDiscountRequestDto,
) => {
	const response = await restRepository.api.userRequestLayoffDiscount(payload);
	return response.data;
};

export const getReskillPriceDocuments = async (priceId: string) => {
	const response = await restRepository.api.getReskillPriceDocuments(priceId);
	return response.data;
};

export const getSignedReskillPriceDocument = async (
	priceId: string,
	documentId: string,
) => {
	const response = await restRepository.api.getSignedReskillPriceDocument(
		priceId,
		documentId,
	);
	return response.data;
};
